import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Eye, X, Trash } from '@phosphor-icons/react';
import { getSubscriptionStatusStyle } from '@/lib/statusColors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminSubscriptionsTab = ({ requests: initialRequests, onUpdateStatus }) => {
  const [requests, setRequests] = useState(initialRequests);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [viewing, setViewing] = useState(null);

  const filtered = requests.filter((r) => {
    if (filter !== 'all' && r.status !== filter) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      return (
        r.request_id?.toLowerCase().includes(s) ||
        r.full_name?.toLowerCase().includes(s) ||
        r.email?.toLowerCase().includes(s) ||
        r.mobile?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  // Sync local state when parent passes new requests
  React.useEffect(() => { setRequests(initialRequests); }, [initialRequests]);

  const handleStatusUpdate = async (requestId, status) => {
    await onUpdateStatus(requestId, status);
    if (viewing?.request_id === requestId) {
      setViewing({ ...viewing, status });
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm(`Permanently delete request ${requestId}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/subscription-requests/${requestId}`, { withCredentials: true });
      setRequests(prev => prev.filter(r => r.request_id !== requestId));
      if (viewing?.request_id === requestId) setViewing(null);
      toast.success('Request deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="subscriptions-management">
      <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
        <h2 className="text-xl font-medium text-[#1C1917] mb-6 font-heading">Fitness Subscription Requests</h2>

        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input type="text" placeholder="Search by Request ID, name, email, mobile..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-xl outline-none focus:border-[#1A4331]" data-testid="sub-search-input" />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl outline-none focus:border-[#1A4331]" data-testid="sub-filter-select">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {filtered.length === 0 ? (
          <p className="text-stone-500 text-center py-12">No subscription requests found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  {['Request ID', 'Customer', 'Plan', 'Start Date', 'Status', 'Date', 'Actions'].map((h) => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-bold uppercase tracking-wider text-stone-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((req) => (
                  <tr key={req.request_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`sub-row-${req.request_id}`}>
                    <td className="py-3 px-3 text-sm font-mono font-bold text-[#1A4331]">{req.request_id}</td>
                    <td className="py-3 px-3 text-sm">
                      <div className="font-medium text-[#1C1917]">{req.full_name}</div>
                      <div className="text-xs text-stone-500">{req.email}</div>
                    </td>
                    <td className="py-3 px-3 text-sm text-stone-700">
                      {(() => {
                        if (req.subscriptions && req.subscriptions.length > 0) {
                          return req.subscriptions.map(s => {
                            if (s.type === 'post-workout') return s.plan === 'weight-loss' ? 'Weight Loss' : 'Muscle Gain';
                            if (s.type === 'lunch') return 'Lunch';
                            if (s.type === 'dinner') return 'Dinner';
                            return s.type;
                          }).join(' + ');
                        }
                        return req.plan_type?.replace('-', ' ') || '—';
                      })()}
                    </td>
                    <td className="py-3 px-3 text-sm text-stone-700">{req.start_date}</td>
                    <td className="py-3 px-3">
                      <span data-testid={`sub-status-badge-${req.request_id}`} className={`text-xs px-3 py-1 rounded-full font-medium ${getSubscriptionStatusStyle(req.status)}`}>{req.status}</span>
                    </td>
                    <td className="py-3 px-3 text-xs text-stone-500">{new Date(req.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-3">
                      <button onClick={() => setViewing(req)} className="text-stone-600 hover:text-[#1A4331] p-1.5 rounded-lg hover:bg-stone-100" title="View" data-testid={`view-sub-${req.request_id}`}>
                        <Eye size={18} weight="duotone" />
                      </button>
                      <button onClick={() => handleDelete(req.request_id)} className="text-stone-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50" title="Delete" data-testid={`delete-sub-${req.request_id}`}>
                        <Trash size={18} weight="duotone" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewing && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setViewing(null)} data-testid="request-detail-modal">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Request ID</p>
                  <p className="text-2xl font-bold text-[#1A4331]">{viewing.request_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleDelete(viewing.request_id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm text-red-600 hover:bg-red-50 border border-red-200 transition"
                    data-testid="modal-delete-sub-btn">
                    <Trash size={14} /> Delete
                  </button>
                  <button onClick={() => setViewing(null)} className="text-stone-500 hover:text-stone-800 p-2"><X size={24} weight="bold" /></button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Plan</p>
                    <p className="font-medium text-[#1C1917]">
                      {viewing.subscriptions && viewing.subscriptions.length > 0
                        ? viewing.subscriptions.map(s => {
                            if (s.type === 'post-workout') return s.plan === 'weight-loss' ? 'Weight Loss' : 'Muscle Gain';
                            if (s.type === 'lunch') return 'Lunch';
                            if (s.type === 'dinner') return 'Dinner';
                            return s.type;
                          }).join(' + ')
                        : (viewing.plan_type?.replace('-', ' ') || '—')}
                    </p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Start Date</p>
                    <p className="font-medium text-[#1C1917]">{viewing.start_date}</p>
                  </div>
                </div>

                <div className="bg-stone-50 rounded-xl p-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Customer Details</p>
                  <p className="font-medium text-[#1C1917]">{viewing.full_name}</p>
                  <p className="text-sm text-stone-600">{viewing.email}</p>
                  <p className="text-sm text-stone-600">{viewing.mobile}</p>
                </div>

                <div className="bg-stone-50 rounded-xl p-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Delivery / Fitness Center</p>
                  {viewing.fitness_center_name ? (
                    <p className="text-sm font-semibold text-[#1A4331]">{viewing.fitness_center_name}</p>
                  ) : (
                    <p className="text-sm text-stone-500 italic">No center selected</p>
                  )}
                  <p className="text-xs text-stone-400 mt-2 uppercase tracking-wider mb-1">Home / Contact Address</p>
                  <p className="text-xs text-stone-600">{viewing.address_line1}</p>
                  {viewing.address_line2 && <p className="text-xs text-stone-600">{viewing.address_line2}</p>}
                  <p className="text-xs text-stone-600">{viewing.city}, {viewing.state} - {viewing.zipcode}</p>
                </div>

                <div className="bg-stone-50 rounded-xl p-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Selected Meal Options</p>
                  {viewing.subscriptions && viewing.subscriptions.length > 0 ? (
                    <div className="space-y-3">
                      {viewing.subscriptions.map((s, i) => {
                        const label = s.type === 'post-workout'
                          ? (s.plan === 'weight-loss' ? 'Post-Workout — Weight Loss' : 'Post-Workout — Muscle Gain')
                          : s.type === 'lunch' ? 'Lunch' : 'Dinner';
                        return (
                          <div key={i}>
                            <p className="text-xs font-bold uppercase tracking-wider text-[#1A4331] mb-1">{label} · {s.billing}</p>
                            <ul className="space-y-1 pl-2">
                              {Object.entries(s.meal_choices || {}).map(([day, meal]) => (
                                <li key={day} className="text-sm"><span className="font-medium text-[#1C1917]">{day}:</span> <span className="text-stone-700">{meal}</span></li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {Object.entries(viewing.selected_options || {}).map(([day, meal]) => (
                        <li key={day} className="text-sm"><span className="font-medium text-[#1C1917]">{day}:</span> <span className="text-stone-700">{meal}</span></li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="border-t border-stone-200 pt-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Update Status</p>
                  <p className="text-xs text-stone-400 mb-3">
                    <span className="font-medium text-amber-700">Accepted</span> &amp; <span className="font-medium text-red-600">Rejected</span> trigger emails to customer + admin.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { s: 'pending',   label: 'Pending',   hint: 'Awaiting review' },
                      { s: 'accepted',  label: 'Accepted',  hint: '📧 Sends confirmation email' },
                      { s: 'active',    label: 'Active',    hint: 'Meals being delivered' },
                      { s: 'completed', label: 'Completed', hint: 'Cycle ended' },
                      { s: 'rejected',  label: 'Rejected',  hint: '📧 Sends rejection email' },
                    ].map(({ s, label, hint }) => (
                      <div key={s} className="flex flex-col items-center gap-0.5">
                        <button
                          onClick={() => handleStatusUpdate(viewing.request_id, s)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${viewing.status === s ? 'bg-[#1A4331] text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
                          data-testid={`status-btn-${s}`}
                          title={hint}
                        >
                          {label}
                        </button>
                        <span className="text-[10px] text-stone-400">{hint}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminSubscriptionsTab;
